// src/components/ClientJobHistory.js
// ADD-ON COMPONENT - Just add this to your existing client page

import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import {
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers"; // Import centralized helpers

const ClientJobHistory = ({ open, onClose, client }) => {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    if (!client) return;

    const jobsQuery = query(collection(db, "jobs"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const allJobs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter jobs for this client
      const clientJobs = allJobs.filter(
        (job) => job.clientId === client.id || job.customerName === client.name
      );

      setJobs(clientJobs);
    });

    return unsubscribe;
  }, [client]);

  if (!client) return null;

  const completedJobs = jobs.filter((j) => j.status === "Completed");
  const pendingJobs = jobs.filter((j) => j.status === "Pending");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <HistoryIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">Job History</Typography>
            <Typography variant="body2" color="text.secondary">
              {client.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "rgba(59,130,246,0.1)" }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {jobs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Jobs
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "rgba(16,185,129,0.1)" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                {completedJobs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center", bgcolor: "rgba(244,63,94,0.1)" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
                {pendingJobs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Recent Jobs:
        </Typography>

        {jobs.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No jobs found for this client
          </Typography>
        ) : (
          <List>
            {jobs.map((job) => (
              <ListItem
                key={job.id}
                sx={{
                  mb: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: getStatusColor(job.status) === "success" ? "success.main" : getStatusColor(job.status) === "warning" ? "warning.main" : "primary.main",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Job #{job.jobReferenceNumber || job.id.slice(-6).toUpperCase()}
                      </Typography>
                      <Chip
                        label={job.status}
                        size="small"
                        color={getStatusColor(job.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        {job.jobTemplateName || job.jobSheetTemplateName || "Service"}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {job.vehicleReg || job.vehicle} •{" "}
                        {job.createdAt?.toDate
                          ? job.createdAt.toDate().toLocaleDateString()
                          : "Date N/A"}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientJobHistory;